import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { XIcon } from '@phosphor-icons/react';

import type { PublishScanResult } from '@/hooks/use-publish-scan';

interface PublishScanDialogProps {
  open: boolean;
  pageTitle: string;
  scanning: boolean;
  error: string | null;
  result: PublishScanResult | null;
  onClose: () => void;
  onConfirmPublish: () => void;
}

// Shown when a page's own Published switch (Page Settings) is turned on —
// unpublishing a page is always safe and skips this dialog entirely.
// Blocking issues (youth PII) cannot be published past; the database
// triggers in supabase/migrations/20260714163000_publish_pii_scan.sql are
// the real backstop if this dialog is ever bypassed, so disabling Confirm
// here is a UX courtesy, not the enforcement itself. Advisory issues are
// shown for awareness but never block Publish.
export function PublishScanDialog({ open, pageTitle, scanning, error, result, onClose, onConfirmPublish }: PublishScanDialogProps) {
  const hasBlocking = Boolean(result && result.blocking.length > 0);
  const hasAdvisory = Boolean(result && result.advisory.length > 0);
  const isClean = Boolean(result) && !hasBlocking && !hasAdvisory;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Publish &ldquo;{pageTitle}&rdquo;?
        <IconButton onClick={onClose} aria-label="Close">
          <XIcon size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {scanning ? (
          <Stack sx={{ alignItems: 'center', py: 4 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Checking this page for youth privacy and content issues…
            </Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : result ? (
          <Stack spacing={2} sx={{ py: 1 }}>
            {isClean ? <Alert severity="success">No issues found — this page is ready to publish.</Alert> : null}

            {hasBlocking ? (
              <Alert severity="error">
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  This page can&apos;t be published yet
                </Typography>
                <Typography variant="body2">
                  The following blocks appear to name a youth member. Mark them Internal or remove the name before publishing.
                </Typography>
              </Alert>
            ) : null}
            {result.blocking.map((issue) => (
              <IssueRow key={issue.blockId} blockType={issue.blockType} description={`Names a youth member: ${issue.matchedNames.join(', ')}`} />
            ))}

            {hasBlocking && hasAdvisory ? <Divider /> : null}

            {hasAdvisory ? (
              <Alert severity="warning">A few things worth a second look before you publish — these won&apos;t block publishing.</Alert>
            ) : null}
            {result.advisory.map((entry) =>
              entry.issues.map((issue, index) => (
                <IssueRow key={`${entry.blockId}-${issue.type}-${index}`} blockType={entry.blockType} description={issue.message} />
              ))
            )}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={scanning || Boolean(error) || hasBlocking || !result}
          onClick={onConfirmPublish}
        >
          {hasAdvisory ? 'Publish anyway' : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function IssueRow({ blockType, description }: { blockType: string; description: string }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {blockType}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  );
}
